import { updateCorporateUsers } from "../../../../app/api/corporate";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../../Lib/Utils/i18n";
import { showNotification } from "../../../../Services/notificationService";
import { getRecoilStateValue } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse } from "../../../../Orchestrator/Types/Index";
import {
    ICorporateOk,
    ICorporateUsersUpdate,
} from "../../../../Orchestrator/Types/Interface/ICorporate";
import { IUserResponse } from "../../../../Orchestrator/Types/Interface/IUser";
import { getCorporateUsersAction } from "./GetCorporateUsersAction";

export interface IUpdateCorporateUsersActionParams {
    update: ICorporateUsersUpdate;
    /** Title key shown on success, so add, edit and remove read differently. */
    successMessageKey: string;
    /** Supporting line under the title, as the design's toast shows. */
    successDescriptionKey?: string;
}

/** Adds or removes a company member via PUT /corporates/{id}/users. */
class UpdateCorporateUsersAction extends BaseAction<
    boolean,
    IUpdateCorporateUsersActionParams
> {
    /**
     * Why the last call failed, so the modal can attach the message to the
     * right field instead of only showing a toast.
     */
    lastFailure: { code: number; message: string } | null = null;

    /** A duplicate address is a field error, not a generic failure. */
    isDuplicateEmailFailure(): boolean {
        if (!this.lastFailure) return false;
        if (this.lastFailure.code === 409) return true;
        return /already|exists|duplicate|registered/i.test(this.lastFailure.message);
    }

    override async run(): Promise<boolean | null> {
        const params = this.params;
        const userData = getRecoilStateValue<IUserResponse>("userDataAtom");
        const corporateId = userData?.corporateId;

        // Cleared before the guard, not after: bailing out with a previous
        // run's failure still set would let isDuplicateEmailFailure() pin
        // "Email already registered" onto an unrelated submission.
        this.lastFailure = null;

        if (!params?.update || corporateId == null) {
            console.warn("[UpdateCorporateUsersAction] Missing company or body; skipping.");
            return null;
        }

        try {
            const response: IApiResponse<ICorporateOk> =
                await updateCorporateUsers(corporateId, params.update);

            if (response.status === "ok") {
                showNotification(
                    "success",
                    getTranslatedMessage(params.successMessageKey),
                    params.successDescriptionKey
                        ? getTranslatedMessage(params.successDescriptionKey)
                        : ""
                );
                // Re-read rather than patching locally: the backend assigns ids
                // and ranks to created members.
                await getCorporateUsersAction.execute();
                return true;
            }

            // apiRequest already raised a notification for the failed request.
            const body = response.body as { message?: string } | undefined;
            this.lastFailure = {
                code: response.code,
                message: body?.message ?? response.error ?? "",
            };
            console.warn("[UpdateCorporateUsersAction] The member update failed.", this.lastFailure);
            return null;
        } catch (error: any) {
            this.lastFailure = { code: 0, message: String(error?.message ?? error) };
            console.error("[UpdateCorporateUsersAction] Error updating members:", error);
            showNotification("error", getTranslatedMessage("companyEmployeeUpdateFailed"), "");
            return null;
        }
    }
}

export const updateCorporateUsersAction = new UpdateCorporateUsersAction();
